import { StateCreator } from "zustand";

import {
  CommandCenterSliceType,
  CommandContextType,
  CommandDefinitionType,
  GlobalStoreStateType,
  PushToCommandStackInputType,
} from "@/state/@types";
import { findNearestContextNode } from "@/hooks/useContextTree";

export const createCommandCenterSlice: StateCreator<
  GlobalStoreStateType,
  [],
  [],
  CommandCenterSliceType
> = (set, get) => ({
  // initial state
  commandInstanceStack: [],
  commandDefinitions: [],
  commandDefinitionIdOfQuickActions: "search",
  commandSearchValue: "",
  commandCenterEnterHandler: null,
  dialogSize: null,
  dialogLayoutMode: null,
  dialogIsFullScreen: false,
  dialogGroupCount: 0,
  isCommandCenterSearching: false,

  // actions
  pushToCommandStack: (input: PushToCommandStackInputType) => {
    const commandDefinitionId = input.commandDefinitionId;
    const context = input.context as CommandContextType | undefined;

    // Auto-capture focus origin before opening the command dialog
    const el = document.activeElement as HTMLElement | null;
    const contextNode = el ? findNearestContextNode(el) : null;
    get().pushFocusOrigin({ contextNode, elementRef: el });

    set((state) => ({
      commandInstanceStack: [
        ...(state.commandInstanceStack || []),
        {
          commandDefinitionId,
          commandInstanceId: crypto.randomUUID(),
          context: context || undefined,
        },
      ],
    }));
  },

  popCommandsFromStack: (popCount: number = 1) => {
    set((state) => ({
      commandInstanceStack: state.commandInstanceStack.slice(0, -popCount),
    }));
  },

  updateCommandContext: (commandInstanceId, context) => {
    set((state) => ({
      commandInstanceStack: state.commandInstanceStack.map((commandInstance) =>
        commandInstance.commandInstanceId === commandInstanceId
          ? {
              ...commandInstance,
              context: {
                ...commandInstance.context,
                ...context,
              } as CommandContextType,
            }
          : commandInstance,
      ),
    }));
  },

  // Command registration and management
  registerCommandDefinition: (command: CommandDefinitionType) => {
    const defs = get().commandDefinitions;
    const idx = defs.findIndex((c) => "id" in c && c.id === command.id);
    if (idx >= 0) {
      // Update existing
      set({
        commandDefinitions: defs.map((c, i) => (i === idx ? command : c)),
      });
    } else {
      // Add new
      set({ commandDefinitions: [...defs, command] });
    }
  },

  updateCommandDefinition: (commandId, command) => {
    set({
      commandDefinitions: get().commandDefinitions.map((existingCommand) =>
        "id" in existingCommand && existingCommand.id === commandId
          ? { ...existingCommand, ...command }
          : existingCommand,
      ),
    });
  },

  unregisterCommandDefinition: (commandId) => {
    set({
      commandDefinitions: get().commandDefinitions.filter(
        (c) => !("id" in c) || c.id !== commandId,
      ),
    });
  },

  setCommandSearchValue: (commandSearchValue) => set({ commandSearchValue }),

  setCommandCenterEnterHandler: (handler) =>
    set({ commandCenterEnterHandler: handler }),

  clearCommandStack: () => {
    get().clearFocusOriginStack();
    set({
      commandInstanceStack: [],
    });
  },
});
